/* global __BUILD_HASH__ */
import { DOS_FONT_8X16, CELL_WIDTH, CELL_HEIGHT } from './dosFont8x16.js';
/**
 * Bitmap font renderer.
 *
 * Converts monospace sprite sheets generated by the build pipeline into draw
 * commands accepted by the low-level `shaderFont` and WebGL context.  No DOM,
 * no React: pure math + typed arrays so it can run on workers or Node tests.
 *
 * @module engine/renderer/bitmapFont
 */
/*
  Canonical Bitmap Font utilities for DopeOS
  
  Two rendering systems:
  1. DOS Text: Mathematical shader rendering - for crisp UI text
  2. ASCII Art: ANSI Shadow FIGfont sprites - for titles and headers, scalable
*/

// --- Helper --------------------------------------------------------------
export function hexToRgb(hex) {
  hex = hex.replace('#', '');
  if (hex.length === 3) hex = hex.split('').map(x => x + x).join('');
  const num = parseInt(hex, 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

// Mathematical text rendering removed - using optimized bitmap fonts

// --- Core DOS bitmap font (for UI text) - DEPRECATED, kept for fallback -
export const bitmapFont = {
  image: null,
  meta: null,
  loaded: false,
  glyphCache: {},
  async load() {
    if (this.loaded) return;
    console.log('Loading DOS bitmap font (fallback)...');
    const v = (typeof __BUILD_HASH__ !== 'undefined' && __BUILD_HASH__) ? `?v=${__BUILD_HASH__}` : '';
    const [img, meta] = await Promise.all([
      new Promise((res, rej) => {
        const i = new Image();
        i.src = `/fonts/bitmap_perfect_dos.png${v}`;
        i.onload = () => res(i);
        i.onerror = rej;
      }),
      fetch(`/fonts/bitmap_perfect_dos.json${v}`).then(r => {
        if (!r.ok) throw new Error('bitmap_perfect_dos.json fetch error');
        return r.json();
      }),
    ]);
    this.image = img;
    this.meta = meta;
    const { cellWidth, cellHeight, columns, charset } = meta;
    const charArray = typeof charset === 'string' ? charset.split('') : Array.isArray(charset) ? charset : [];
    const [r, g, b] = hexToRgb('#39FF14');
    
    // Build glyph cache (minimal for fallback only)
    charArray.forEach((ch, i) => {
      const sx = (i % columns) * cellWidth;
      const sy = Math.floor(i / columns) * cellHeight;
      const glyph = document.createElement('canvas');
      glyph.width = cellWidth;
      glyph.height = cellHeight;
      const gctx = glyph.getContext('2d');
      gctx.drawImage(img, sx, sy, cellWidth, cellHeight, 0, 0, cellWidth, cellHeight);
      const imgData = gctx.getImageData(0, 0, cellWidth, cellHeight);
      for (let p = 0; p < imgData.data.length; p += 4) {
        if (imgData.data[p + 3] > 0) {
          imgData.data[p] = r;
          imgData.data[p + 1] = g;
          imgData.data[p + 2] = b;
        }
      }
      gctx.putImageData(imgData, 0, 0);
      this.glyphCache[ch] = glyph;
    });
    this.loaded = true;
    console.log('DOS bitmap font loaded (fallback mode)');
  },
  // Fallback bitmap rendering (only used if mathematical rendering unavailable)
  drawText(ctx, text, x, y, scale = 1) {
    if (!this.loaded) return;
    const { cellWidth } = this.meta;
    ctx.save();
    if (scale !== 1) {
      ctx.scale(scale, scale);
      x /= scale;
      y /= scale;
    }
    
    let currentX = 0;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      const glyph = this.glyphCache[ch];
      if (!glyph) continue;
      
      ctx.drawImage(glyph, x + currentX, y);
      
      // Improved kerning for DOS text - better letter spacing for legibility
      let charSpacing = cellWidth * 1.2; // Increased from 0.7 to 1.2 for better spacing
      
      // Special handling for specific narrow characters
      const narrowChars = ['I', 'l', '1', '!', '|', ':', ';', ','];
      if (narrowChars.includes(ch)) {
        charSpacing = cellWidth * 0.9;
      } else if (ch === '.') {
        // Extra spacing for dots so ellipsis "..." looks distinct at any scale
        charSpacing = cellWidth * 1.6;
      }
      
      // Special handling for space character
      if (ch === ' ') {
        charSpacing = cellWidth * 1.0; // Increased from 0.6 to 1.0
      }
      
      currentX += charSpacing;
    }
    ctx.restore();
  },
  measureText(text) {
    if (!this.loaded || !this.meta) return { width: 0, height: 16 };
    const { cellWidth, cellHeight } = this.meta;
    
    let totalWidth = 0;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      
      // Same spacing logic as drawText - improved for legibility
      let charSpacing = cellWidth * 1.2; // Increased from 0.7
      const narrowChars = ['I', 'l', '1', '!', '|', ':', ';', ','];
      if (narrowChars.includes(ch)) {
        charSpacing = cellWidth * 0.9;
      } else if (ch === '.') {
        charSpacing = cellWidth * 1.6;
      }
      if (ch === ' ') {
        charSpacing = cellWidth * 1.0;
      }
      
      totalWidth += charSpacing;
    }
    
    return { width: totalWidth, height: cellHeight };
  }
};

// --- ASCII Art FIGfont System (for headers/titles) ----------------------
export const asciiArtFont = {
  figfont: null,
  loaded: false,
  glyphCache: {},
  charHeight: 7, // ANSI Shadow height
  async load() {
    if (this.loaded) return;
    console.log('Loading ASCII art font...');
    try {
      // Try spritesheet first since it has correct character mapping
      const v = (typeof __BUILD_HASH__ !== 'undefined' && __BUILD_HASH__) ? `?v=${__BUILD_HASH__}` : '';
      await this.loadFromSpritesheet(v);
      console.log('ASCII art spritesheet loaded successfully');
    } catch (error) {
      console.warn('[bitmapFont] Spritesheet failed, trying FIGfont fallback:', error);
      try {
        const v = (typeof __BUILD_HASH__ !== 'undefined' && __BUILD_HASH__) ? `?v=${__BUILD_HASH__}` : '';
        const response = await fetch(`/fonts/figlet/ANSI_Shadow.flf${v}`);
        const figfontData = await response.text();
        this.parseFIGfont(figfontData);
        this.loaded = true;
        console.log('ASCII art FIGfont fallback loaded');
      } catch (figfontError) {
        console.error('Both spritesheet and FIGfont failed:', figfontError);
      }
    }
  },
  
  parseFIGfont(data) {
    const lines = data.split('\n');
    const header = lines[0];
    const match = header.match(/flf2a\$?\s+(\d+)\s+(\d+)\s+(\d+)/);
    if (!match) throw new Error('Invalid FIGfont header');
    
    const height = parseInt(match[1]);
    const baseline = parseInt(match[2]);
    
    console.log(`FIGfont loaded: height=${height}, baseline=${baseline}`);
    
    const chars = {};
    let lineIndex = 1;
    
    // Parse each character (starting with space = ASCII 32)
    // The FIGfont appears to be offset by +1, so we need to adjust
    for (let ascii = 32; ascii <= 126; ascii++) {
      const char = String.fromCharCode(ascii);
      const charLines = [];
      
      for (let i = 0; i < height; i++) {
        if (lineIndex < lines.length) {
          let line = lines[lineIndex];
          // Remove trailing @ characters and newlines
          line = line.replace(/@+\s*$/, '');
          charLines.push(line);
        }
        lineIndex++;
      }
      
      // Calculate character width from the widest line
      const width = Math.max(...charLines.map(line => line.length));
      
      chars[char] = {
        lines: charLines,
        width: width,
        height: height
      };
    }
    
    this.figfont = {
      chars: chars,
      height: height,
      baseline: baseline
    };
    
    this.charHeight = height;
  },
  
  async loadFromSpritesheet(versionParam = '') {
    console.log('Loading ASCII art from spritesheet...');
    const [image, metadata] = await Promise.all([
      this.loadImage(`/fonts/asciiart_spritesheet.png${versionParam}`),
      fetch(`/fonts/asciiart_spritesheet.json${versionParam}`).then(r => r.json())
    ]);
    
    console.log('Spritesheet metadata:', {
      charset: metadata.charset,
      cellWidth: metadata.cellWidth,
      cellHeight: metadata.cellHeight,
      columns: metadata.columns,
      rows: metadata.rows,
      totalChars: Object.keys(metadata.charmap).length
    });

    this.glyphCache = {};
    
    for (const [char, charInfo] of Object.entries(metadata.charmap)) {
      const canvas = document.createElement('canvas');
      canvas.width = charInfo.w;
      canvas.height = charInfo.h;
      const ctx = canvas.getContext('2d');
      
      ctx.drawImage(
        image,
        charInfo.x,
        charInfo.y,
        charInfo.w,
        charInfo.h,
        0,
        0,
        charInfo.w,
        charInfo.h
      );
      
      // Re-tint white glyphs to canonical CRT green so they match DOS text.
      const [r, g, b] = hexToRgb('#39FF14');
      const imgData = ctx.getImageData(0, 0, charInfo.w, charInfo.h);
      const data = imgData.data;
      for (let p = 0; p < data.length; p += 4) {
        if (data[p + 3] > 0) {
          data[p] = r;
          data[p + 1] = g;
          data[p + 2] = b;
        }
      }
      ctx.putImageData(imgData, 0, 0);
      
      this.glyphCache[char] = {
        canvas,
        width: charInfo.w,
        height: charInfo.h,
      };
    }
    
    console.log(`ASCII art spritesheet loaded: ${Object.keys(this.glyphCache).length} characters`);
    console.log('Available characters:', Object.keys(this.glyphCache).join(''));
    
    this.loaded = true;
  },

  loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  },
  
  renderText(ctx, text, x, y, scale = 1) {
    if (!this.loaded) return { width: 0, height: 0 };
    
    ctx.save();
    if (scale !== 1) {
      ctx.scale(scale, scale);
      x /= scale;
      y /= scale;
    }
    
    let totalWidth = 0;
    let maxHeight = 0;
    
    // Uniform scale factor for ASCII art glyphs. This central definition
    // allows us to reuse the same scale for both bitmap-sprite glyphs and
    // the monospace fallback below.
    const charScale = 0.3;
    
    // Prioritize spritesheet over FIGfont for ASCII art
    if (this.glyphCache) {
      // Use spritesheet for ASCII art
      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        const glyph = this.glyphCache[char];
        if (glyph) {
          // Scale down the large ASCII art to fit screen (charScale defined above)
          const charWidth = glyph.width * charScale;
          const charHeight = glyph.height * charScale;
          
          // Enhanced kerning for better readability and legibility
          let spacing = charWidth * 0.75;
          
          const narrowChars = ['I', 'l', '1', '!', '|', ':', ';', ','];
          if (char === '.') {
            // Extra spacing so ellipsis "..." looks distinct at high scales
            spacing *= 1.6;
          } else if (narrowChars.includes(char)) {
            spacing *= 1.0; // minimal adjustment for other narrow chars
          } else {
            const wideChars = ['W', 'M', '@', '%', '#'];
            if (wideChars.includes(char)) {
              spacing *= 1.5;
            } else {
              spacing *= 1.3;
            }
          }
          
          ctx.save();
          ctx.scale(charScale, charScale);
          ctx.drawImage(glyph.canvas, (x + totalWidth) / charScale, y / charScale);
          ctx.restore();
          
          totalWidth += spacing;
          maxHeight = Math.max(maxHeight, charHeight);
        } else {
          // Fallback measurement for characters absent in the spritesheet.
          const fallbackWidth = 16 * charScale * 0.75;
          totalWidth += fallbackWidth;
          maxHeight = Math.max(maxHeight, 16 * charScale);
        }
      }
    } else if (this.figfont) {
      // Fallback to FIGfont if spritesheet not available
      const charSpacing = 2;
      let currentX = x;
      
      const charDataArray = [];
      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        const charData = this.figfont.chars[char] || this.figfont.chars[' '];
        if (charData) {
          charDataArray.push(charData);
        }
      }
      
      ctx.font = '12px "SF Mono", "Monaco", "Inconsolata", "Fira Code", monospace';
      ctx.fillStyle = '#39FF14';
      ctx.textBaseline = 'top';
      
      charDataArray.forEach((charData) => {
        charData.lines.forEach((line, row) => {
          const lineY = y + (row * 14);
          if (line && line.trim()) {
            ctx.fillText(line, currentX, lineY);
          }
        });
        
        currentX += (charData.width * 6) + charSpacing;
        maxHeight = Math.max(maxHeight, this.charHeight * 14);
      });
      
      totalWidth = currentX - x;
    } else {
      console.warn('No ASCII art renderer available');
    }
    
    ctx.restore();
    return { width: totalWidth, height: maxHeight };
  },
  
  measureText(text) {
    if (!this.loaded || !text) return { width: 0, height: 0 };
    
    // Prioritize spritesheet over FIGfont for ASCII art
    if (this.glyphCache) {
      let totalWidth = 0;
      let maxHeight = 0;
      const charScale = 0.3; // Same scale as renderText
      
      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        const glyph = this.glyphCache[char];
        if (glyph) {
          const charWidth = glyph.width * charScale;
          
          // Enhanced kerning for better readability and legibility
          let spacing = charWidth * 0.75;
          
          const narrowChars = ['I', 'l', '1', '!', '|', ':', ';', ','];
          if (char === '.') {
            spacing *= 1.6; // keep in sync with renderText
          } else if (narrowChars.includes(char)) {
            spacing *= 1.0;
          } else {
            const wideChars = ['W', 'M', '@', '%', '#'];
            if (wideChars.includes(char)) {
              spacing *= 1.5;
            } else {
              spacing *= 1.3;
            }
          }
          
          totalWidth += spacing;
          maxHeight = Math.max(maxHeight, glyph.height * charScale);
        } else {
          // Fallback measurement for characters absent in the spritesheet.
          const fallbackWidth = 16 * charScale * 0.75;
          totalWidth += fallbackWidth;
          maxHeight = Math.max(maxHeight, 16 * charScale);
        }
      }
      
      return { width: totalWidth, height: maxHeight };
    } else if (this.figfont) {
      const charSpacing = 2;
      let totalWidth = 0;
      
    for (let i = 0; i < text.length; i++) {
        const char = text[i];
        const charData = this.figfont.chars[char];
        if (charData) {
          totalWidth += (charData.width * 6) + charSpacing;
        }
      }
      
      if (totalWidth > charSpacing) {
        totalWidth -= charSpacing;
      }
      
      return { 
        width: totalWidth, 
        height: this.charHeight * 14
      };
    }
    
    return { width: 0, height: 0 };
  },
};

// --- Plasma density characters (for blob animation) ---------------------
export const plasmaDensity = ' .,:;i1tfLCG08@#%';

export const plasmaGlyphCache = {
  loaded: false,
  glyphs: {},
  async load() {
    if (this.loaded) return;
    console.log('Loading plasma density glyphs...');
    
    // Wait for DOS font to be loaded
    if (!bitmapFont.loaded) {
      await bitmapFont.load();
    }
    
    // Create plasma-specific glyphs from DOS font
    for (const char of plasmaDensity) {
      if (bitmapFont.glyphCache[char]) {
        this.glyphs[char] = bitmapFont.glyphCache[char];
      }
    }
    
    this.loaded = true;
    console.log('Plasma density glyphs loaded');
  }
};

// Legacy compatibility function removed - use DopeTextRenderer.measureText instead

export function drawFittedText(ctx, text, x, y, maxW, font = bitmapFont) {
  if (!font.loaded) return;
  const measurement = font.measureText(text);
  if (measurement.width <= maxW) {
    font.drawText(ctx, text, x, y);
    return;
  }
  const scale = maxW / measurement.width;
  font.drawText(ctx, text, x, y, scale);
}

function glyphFor(codePoint) {
  const index = codePoint - 32;
  return DOS_FONT_8X16[index] || DOS_FONT_8X16[31]; // printable fallback: '?'
}

function renderCellText(ctx, text, x, y, scale = 1) {
  let cursorX = x;
  const pixel = scale;

  for (let i = 0; i < text.length; i += 1) {
    const glyph = glyphFor(text.charCodeAt(i));

    for (let row = 0; row < CELL_HEIGHT; row += 1) {
      const bits = glyph[row];
      if (!bits) continue;

      for (let col = 0; col < CELL_WIDTH; col += 1) {
        if (bits & (1 << (CELL_WIDTH - 1 - col))) {
          ctx.fillRect(cursorX + col * pixel, y + row * pixel, pixel, pixel);
        }
      }
    }

    cursorX += CELL_WIDTH * pixel;
  }

  return {
    width: text.length * CELL_WIDTH * scale,
    height: CELL_HEIGHT * scale,
  };
}

// --- Public API for the canonical text rendering system -----------------
export const DopeTextRenderer = {
  // Initialize all font systems
  async init() {
    if (typeof __DEV__ !== 'undefined' && __DEV__) {
      console.log('[BitmapFont] Initializing canonical 8x16 bitmap text renderer...');
    }
    
    try {
      console.log('[BitmapFont] Loading font systems...');
      
      const startTime = performance.now();
      await Promise.all([
        bitmapFont.load().catch(err => {
          console.error('[BitmapFont] Error loading bitmap font:', err);
          throw err;
        }),
        asciiArtFont.load().catch(err => {
          console.error('[BitmapFont] Error loading ASCII art font:', err);
          throw err;
        }),
        plasmaGlyphCache.load().catch(err => {
          console.error('[BitmapFont] Error loading plasma glyph cache:', err);
          throw err;
        })
      ]);
      const loadTime = performance.now() - startTime;
      
      console.log(`[BitmapFont] All text rendering systems loaded in ${loadTime.toFixed(2)}ms`);
      
      // Validate that critical fonts are available
      if (!bitmapFont.loaded || !asciiArtFont.loaded) {
        console.error('[BitmapFont] Critical fonts failed to load properly');
        return false;
      }
      
      return true;
    } catch (err) {
      console.error('[BitmapFont] Failed to initialize text renderer:', err);
      return false;
    }
  },
  
  // Canonical DOS text rendering.
  // Every printable ASCII character consumes exactly one 8x16 cell.
  renderDOSText(ctx, text, x, y, scale = 1) {
    try {
      // Apply canonical dimming unless the caller already changed alpha.
      const DEFAULT_ALPHA = 0.6942;
      const originalAlpha = ctx.globalAlpha;
      if (originalAlpha === 1) {
        ctx.globalAlpha = DEFAULT_ALPHA;
      }

      const res = renderCellText(ctx, text, x, y, scale);

      ctx.globalAlpha = originalAlpha; // restore
      return res;
    } catch (err) {
      console.error('[BitmapFont] Error rendering DOS text:', err);
      return { width: text.length * CELL_WIDTH * scale, height: CELL_HEIGHT * scale };
    }
  },
  
  // Render ASCII art text (unchanged, sprites work perfectly)
  renderASCIIArt(ctx, text, x, y, scale = 1) {
    try {
      return asciiArtFont.renderText(ctx, text, x, y, scale);
    } catch (err) {
      console.error('[BitmapFont] Error rendering ASCII art:', err);
      
      // Fallback to basic rendering
      try {
        ctx.save();
        ctx.font = `bold ${18 * scale}px monospace`;
        ctx.fillStyle = '#39FF14';
        ctx.textBaseline = 'top';
        ctx.fillText(text, x, y);
        ctx.restore();
        
        return { width: ctx.measureText(text).width, height: 24 * scale };
      } catch (fallbackErr) {
        console.error('[BitmapFont] ASCII art fallback rendering failed:', fallbackErr);
        return { width: 0, height: 0 };
      }
    }
  },
  
  // Fixed-cell measurement: every character is exactly one 8x16 cell.
  measureDOSText(text, scale = 1) {
    return {
      width: text.length * CELL_WIDTH * scale,
      height: CELL_HEIGHT * scale,
    };
  },
  
  measureASCIIArt(text) {
    try {
      return asciiArtFont.measureText(text);
    } catch (err) {
      console.error('[BitmapFont] Error measuring ASCII art:', err);
      
      // Fallback measurement
      const avgCharWidth = 16;
      return { width: text.length * avgCharWidth, height: 24 };
    }
  },
  
  // Check if systems are ready
  isReady() {
    return bitmapFont.loaded && asciiArtFont.loaded && plasmaGlyphCache.loaded;
  }
};

// Legacy exports removed - use DopeTextRenderer instead
